<template>
  <div class="specific-products-container">
    <!-- 引入头部导航组件 -->
    <AppHeader />
    
    <!-- 面包屑导航 -->
    <div class="breadcrumb scroll-animate">
      <div class="breadcrumb-container">
        <div class="breadcrumb-content">
          <span class="breadcrumb-item"><router-link to="/">千手医疗首页</router-link></span>
          <span class="breadcrumb-separator">></span>
          <span class="breadcrumb-item"><router-link to="/product-application">产品中心</router-link></span>
          <span class="breadcrumb-separator">></span>
          <span class="breadcrumb-item">{{ productTitle || '产品详情' }}</span>
        </div>
      </div>
    </div>
    
    <!-- 主要内容区域 -->
    <main class="main-content">
      <!-- 产品标题和型号（仅皮肤镜工作站展示型号） -->
      <div class="product-title-section scroll-animate">
        <h1>{{ productTitle || '双光源LED光谱治疗仪' }}</h1>
        <div v-if="showProductModel" class="product-model">型号: {{ displayProductModel }}</div>
      </div>
      
      <!-- 产品详情内容 -->
      <div class="product-detail-container scroll-animate">
        <!-- 左侧媒体区域（图片 / 视频同排） -->
        <div class="product-image-section">
          <!-- 主图 / 主视频展示 -->
          <video
            v-if="isCurrentVideo"
            :src="currentImage"
            class="main-product-image main-product-video"
            controls
            playsinline
            preload="metadata"
          />
          <img
            v-else
            :src="currentImage"
            alt="产品主图"
            class="main-product-image"
            @click="openImageModal"
          />
          
          <div class="product-gallery">
            <div 
              v-for="(image, index) in productImages" 
              :key="index"
              class="gallery-item"
              :class="{ active: currentIndex === index, 'is-video': isVideoUrl(image) }"
              @click="selectImage(index)"
            >
              <video
                v-if="isVideoUrl(image)"
                :src="image"
                class="gallery-image"
                muted
                playsinline
                preload="metadata"
              />
              <img
                v-else
                :src="image"
                :alt="`产品图片 ${index + 1}`"
                class="gallery-image"
              />
            </div>
          </div>
        </div>
        
        <!-- 右侧产品信息区域 -->
        <div class="product-info-section">
          <!-- 适用范围 -->
          <div class="info-section">
            <h3 class="info-title">适用范围</h3>
            <div class="info-content">
              <p>{{ currentProductDetails ? currentProductDetails.scope : '加载中...' }}</p>
            </div>
          </div>
          
          <!-- 产品详细介绍 -->
          <div class="info-section">
            <h3 class="info-title">产品详细介绍</h3>
            <div class="info-content">
              <p>{{ currentProductDetails ? currentProductDetails.description : '加载中...' }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 产品特性展示区域 -->
      <div class="product-features-section scroll-animate">
        <h2 class="features-section-title">深入了解</h2>
        
        <!-- 只有当featureImages有数据时才渲染（按实际数量展示，避免不足 5 条时报错） -->
        <template v-if="featureImages && featureImages.length > 0">
          <!-- 第一行：大图展示 -->
          <div v-if="featureImages[0]" class="features-row full-width-row">
            <div class="feature-item full-width-item">
              <div class="feature-image-container">
                <img :src="featureImages[0].image" :alt="featureImages[0].title" class="feature-image" />
                <div class="feature-text">
                  <h3 class="feature-title">{{ featureImages[0].title }}</h3>
                  <p class="feature-description">{{ featureImages[0].description }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 第二行：两张图片，比例4-6 -->
          <div v-if="featureImages[1] || featureImages[2]" class="features-row two-columns-row">
            <div v-if="featureImages[1]" class="feature-item column-4">
              <div class="feature-image-container">
                <img :src="featureImages[1].image" :alt="featureImages[1].title" class="feature-image" />
                <div class="feature-text">
                  <h3 class="feature-title">{{ featureImages[1].title }}</h3>
                  <p class="feature-description">{{ featureImages[1].description }}</p>
                </div>
              </div>
            </div>
            <div v-if="featureImages[2]" class="feature-item column-6">
              <div class="feature-image-container">
                <img :src="featureImages[2].image" :alt="featureImages[2].title" class="feature-image" />
                <div class="feature-text">
                  <h3 class="feature-title">{{ featureImages[2].title }}</h3>
                  <p class="feature-description">{{ featureImages[2].description }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 第三行：两张图片，比例6-4 -->
          <div v-if="featureImages[3] || featureImages[4]" class="features-row two-columns-row">
            <div v-if="featureImages[3]" class="feature-item column-6">
              <div class="feature-image-container">
                <img :src="featureImages[3].image" :alt="featureImages[3].title" class="feature-image" />
                <div class="feature-text">
                  <h3 class="feature-title">{{ featureImages[3].title }}</h3>
                  <p class="feature-description">{{ featureImages[3].description }}</p>
                </div>
              </div>
            </div>
            <div v-if="featureImages[4]" class="feature-item column-4">
              <div class="feature-image-container">
                <img :src="featureImages[4].image" :alt="featureImages[4].title" class="feature-image" />
                <div class="feature-text">
                  <h3 class="feature-title">{{ featureImages[4].title }}</h3>
                  <p class="feature-description">{{ featureImages[4].description }}</p>
                </div>
              </div>
            </div>
          </div>
        </template>
        
        <!-- 加载中的提示 -->
        <template v-else>
          <div class="features-loading">
            <p>加载产品特性中...</p>
          </div>
        </template>
      </div>
    </main>
    
    <!-- 引入底部组件 -->
    <AppFooter />
  </div>
</template>

<script>
// 导入头部和底部组件
import AppHeader from './AppHeader.vue'
import AppFooter from './AppFooter.vue'
import { formatImg, isVideoUrl } from '@/utils/formatImg'
import { getProductGalleryByModuleId } from '@/api/pageImages'
import { PRODUCT_PAGE_NAME, resolveProductModuleId } from '@/constants/pageImageCodes'

export default {
  name: 'SpecificProducts',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      productTitle: '',
      productModel: '',
      currentProductDetails: null,
      productImages: [
        require('@/assets/images/homepage/spec-pics/spec-pic1.png'),
        require('@/assets/images/homepage/spec-pics/spec-pic2.png'),
        require('@/assets/images/homepage/spec-pics/spec-pic3.png'),
        require('@/assets/images/homepage/spec-pics/spec-pic4.jpg'),
        require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
      ],
      currentIndex: 0,
      // 产品特性展示图片数据
      featureImages: [],
      // 存储已显示的元素
      revealedElements: [],
      // 产品详情数据库，包含每个产品的详细信息
      productDetails: {
        
        // 皮肤镜图像处理工作站示例数据
        '皮肤镜图像处理工作站 SMS-3000Ⅰ': {
          scope: '适用于皮肤科医生对患者皮肤病变进行观察、分析和记录，辅助皮肤疾病的诊断。',
          description: 'SMS-3000Ⅰ型皮肤镜图像处理工作站采用先进的光学成像技术，提供高清皮肤镜图像。该系统配备专业的图像分析软件，支持图像采集、存储、管理和分析功能，有助于医生更准确地诊断皮肤疾病。',
          features: [
            {
              title: '高清成像系统',
              description: '采用先进的光学成像技术，提供清晰、细腻的皮肤镜图像，能够捕捉微小的皮肤病变细节。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '专业图像分析软件',
              description: '配备功能强大的图像分析软件，支持图像标注、测量和对比分析，提高诊断准确性。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '大容量数据存储',
              description: '内置大容量存储空间，可存储大量患者图像资料，支持快速检索和管理。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '多模式成像',
              description: '支持普通模式、偏振模式等多种成像模式，适应不同类型皮肤病变的观察需求。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '网络数据共享',
              description: '支持局域网内数据共享，便于多科室协作和远程会诊。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        '皮肤镜图像处理工作站 SMS-3000Ⅱ': {
          scope: '适用于皮肤科医生对患者皮肤病变进行观察、分析和记录，辅助皮肤疾病的诊断。',
          description: 'SMS-3000Ⅱ型皮肤镜图像处理工作站是SMS-3000Ⅰ的升级版本，提供更高分辨率的图像质量和更丰富的分析功能。该系统支持多模式成像，并具备智能辅助诊断功能，显著提高诊断效率和准确性。',
          features: [
            {
              title: '超高分辨率成像',
              description: '升级的光学系统提供更高分辨率图像，能够捕捉皮肤表面微小的结构变化，提高早期病变检出率。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '智能辅助诊断',
              description: '内置AI辅助诊断算法，能够对常见皮肤病变进行初步分析和分类，提供诊断参考建议。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '多光谱成像技术',
              description: '支持多种光谱成像，能够观察到皮肤深层结构，提供更全面的病变信息。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '智能随访对比',
              description: '自动比对同一患者不同时期的图像，直观显示病变变化情况，评估治疗效果。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '云数据同步',
              description: '支持云端数据存储和同步，实现跨设备访问和远程会诊功能。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        '皮肤镜图像处理工作站 SMS-6000Ⅰ': {
          scope: '适用于皮肤科医生对患者皮肤病变进行观察、分析和记录，辅助皮肤疾病的诊断，特别适用于疑难病例的诊断。',
          description: 'SMS-6000Ⅰ型皮肤镜图像处理工作站是一款高端皮肤镜系统，采用最新的数字成像技术和人工智能辅助诊断算法。该系统提供超高分辨率图像，并支持多光谱成像，能够捕捉到皮肤深层的细微变化，为疑难病例的诊断提供有力支持。',
          features: [
            {
              title: '超高清数字成像系统',
              description: '采用尖端光学技术和高像素传感器，提供超高清晰度的皮肤镜图像，呈现前所未有的细节。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '高级AI诊断引擎',
              description: '配备深度学习算法，能够识别复杂的皮肤病变特征，对疑难病例提供精准的辅助诊断建议。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '多模态融合成像',
              description: '结合多种成像技术，包括反射式共聚焦显微镜功能，能够观察到皮肤真皮层结构。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '3D图像重建',
              description: '支持三维图像重建功能，直观显示病变的立体结构和分布情况。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '专家知识库集成',
              description: '内置海量皮肤病例数据库，支持相似病例检索和参考，辅助医生做出更准确的诊断。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        // 皮肤镜示例数据
        '触屏式皮肤镜': {
          scope: '适用于皮肤科医生对患者皮肤病变进行观察和初步分析，可用于门诊、病房等多种场合。',
          description: '触屏式皮肤镜采用触摸屏操作，使用方便快捷。该设备体积小巧，成像清晰，支持图像实时显示和简单测量功能，是皮肤科医生日常诊断的得力助手。',
          features: [
            {
              title: '触摸屏操作界面',
              description: '采用直观的触摸屏设计，操作简单便捷，医生可以通过触摸完成图像采集和简单分析。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '便携式设计',
              description: '体积小巧，重量轻，便于在门诊、病房等不同场所移动使用。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '实时图像显示',
              description: '支持图像实时显示功能，医生可以即时观察皮肤病变情况。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '简易测量工具',
              description: '内置简单的图像测量工具，可测量病变大小和形态特征。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '图像存储功能',
              description: '支持图像保存和导出，方便病历记录和后续分析。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        '手持式皮肤镜': {
          scope: '适用于皮肤科医生对患者皮肤病变进行便携式观察，特别适合出诊和社区医疗服务。',
          description: '手持式皮肤镜是一款便携型皮肤检查设备，体积小、重量轻，便于携带。该设备采用高性能光学镜头，提供清晰的皮肤镜图像，并支持与智能手机或平板电脑连接，实现图像的保存和分享。',
          features: [
            {
              title: '超轻便携设计',
              description: '重量仅几百克，体积小巧，可放入口袋，非常适合出诊和社区医疗服务。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '手机/平板连接',
              description: '配备通用接口，可与智能手机或平板电脑连接，利用移动设备的屏幕显示图像。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '高性能光学镜头',
              description: '采用高质量光学镜头，提供清晰的放大图像，保证观察效果。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '可充电电池',
              description: '内置可充电锂电池，一次充电可连续使用数小时，满足出诊需求。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '图像分享功能',
              description: '通过移动设备支持图像的即时分享，便于远程会诊和病例讨论。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        '便携式皮肤镜': {
          scope: '适用于皮肤科医生对患者皮肤病变进行便携式观察，适合出诊、社区医疗服务和远程会诊。',
          description: '便携式皮肤镜集便携性和专业性于一体，配备可充电电池，支持长时间工作。该设备支持无线传输功能，可将图像实时传输至电脑或移动设备，便于远程会诊和病例讨论。',
          features: [
            {
              title: '专业级便携设计',
              description: '兼顾专业性和便携性，在保持小巧体积的同时提供高质量的图像效果。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '无线传输功能',
              description: '支持Wi-Fi或蓝牙无线传输，可将图像实时发送至电脑、平板或手机。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '长效电池续航',
              description: '大容量电池设计，支持连续工作8小时以上，满足全天出诊需求。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '远程会诊支持',
              description: '配合专用软件，支持远程会诊功能，专家可实时查看图像并提供指导。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '多光源照明',
              description: '配备多种光源模式，适应不同类型皮肤病变的观察需求。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        '面部检测仪': {
          scope: '适用于面部皮肤状态的全面分析，包括皱纹、色斑、毛孔、水分含量等，为美容和护肤提供科学依据。',
          description: '面部检测仪采用先进的图像分析技术和人工智能算法，能够对面部皮肤进行全面、精准的分析。该设备可生成详细的面部皮肤评估报告，帮助制定个性化的护肤方案。',
          features: [
            {
              title: '多光谱成像技术',
              description: '采用RGB、UV、偏振光等多种光谱成像，全面分析皮肤各层结构和问题。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '3D面部扫描',
              description: '支持三维面部扫描功能，全方位评估面部轮廓和皮肤状况。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '智能分析算法',
              description: '自动识别和分析皱纹、色斑、毛孔、痤疮等皮肤问题，量化评估严重程度。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '对比分析功能',
              description: '支持治疗前后的图像对比，直观显示治疗效果。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '专业报告生成',
              description: '自动生成详细的皮肤分析报告，包含问题分析和改善建议。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        'visia': {
          scope: '适用于专业皮肤美容机构对面部皮肤进行高精度分析，评估皮肤老化程度和问题肌肤状况。',
          description: 'Visia面部检测仪是一款国际知名的皮肤分析设备，采用先进的光学成像和分析技术，能够捕捉皮肤表面和深层的图像信息。该设备可分析皮肤纹理、色素沉着、毛细血管扩张等多种指标，并提供量化的评估结果，是皮肤美容领域的金标准设备。',
          features: [
            {
              title: '标准皮肤分析系统',
              description: '采用国际公认的皮肤分析标准，提供客观、准确的皮肤状况评估。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '全方位光谱成像',
              description: '采用VISIA专利的多光谱成像技术，可检测紫外线损伤、毛孔、皱纹等多项指标。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '数字模拟预测',
              description: '可模拟未来5-7年的皮肤老化趋势，帮助患者了解皮肤变化。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '数据库比对分析',
              description: '将检测结果与同年龄、同性别人群数据库进行比对，评估相对皮肤状况。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '专业治疗方案建议',
              description: '根据分析结果，提供个性化的皮肤护理和治疗方案建议。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        // 言语认知系列（图一文案 → 便携式；图二文案 → 平板式）
        '言语认知便携式': {
          scope: '适用于言语/认知障碍评估与训练的便携方案，适合上门服务、社区筛查及科室间灵活使用。',
          description: '设备配置参数：★言语康复评估与训练系统软件（1套）：包括系统介绍、病历管理、筛查评估、康复训练四大模块；★平板组件（1套）：10英寸款/12英寸款/14英寸款，不低于8+128，配电容笔、键盘。亦可选配认知康复评估与训练系统软件。',
          features: [
            {
              title: '极致便携性',
              description: '重量通常 < 1kg，可单手操作，搭配磁吸键盘后可放入随身包，适合上门服务或社区筛查。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '触控交互与应用生态',
              description: '支持手势操作、手写笔标注，适合儿童或手部功能障碍患者；可搭配轻量化发音游戏、语音跟读等训练工具。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '续航能力',
              description: '续航 8-12 小时，支持快充，适合全天移动使用，无需频繁充电。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '成本优势',
              description: '基础型号价格较低，搭配无线麦克风等配件后总成本显著低于台车或笔记本方案。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            }
          ]
        },
        '言语认知平板式': {
          scope: '适用于言语/认知障碍评估与训练的笔记本电脑方案，兼顾兼容性、操作便利与便携性。',
          description: '设备配置参数：★言语康复评估与训练系统软件（1套）：包括系统介绍、病历管理、筛查评估、康复训练四大模块；★笔记本电脑组件（1套）：Windows 10；12代 i5；14英寸高清屏幕/15.6英寸款；16GB 内存；256G 固态存储。亦可选配认知康复评估与训练系统软件。',
          features: [
            {
              title: '兼容性好',
              description: '具备 USB、HDMI 等多种接口，可方便连接麦克风、摄像头、打印机等外设，并兼容多种言语评估训练软件。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '操作简便',
              description: '操作系统界面友好，安装设置简单，降低使用门槛；物理键盘与触控板输入快，适合记录评估报告或实时调整训练参数。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '便携性较好',
              description: '相比台车产品体积小巧、重量较轻，康复师可携带至不同科室、患者家中或外出交流，随时开展评估训练。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            }
          ]
        },
        '言语认知单屏幕': {
          scope: '适用于医疗机构开展言语/认知障碍诊治与康复训练的单屏幕台车一体化方案。',
          description: '单屏幕工作站集成语言障碍/认知障碍诊治软件，包含用户管理、筛查评估、康复训练、报告中心等核心模块，并配备麦克风、打印机等外设，满足临床诊治与训练一体化需求。',
          features: [
            {
              title: '用户管理',
              description: '完整的病历与用户信息管理，便于随访与长期康复跟踪。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '筛查评估',
              description: '提供标准化筛查与评估流程，辅助快速完成诊断判断。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '康复训练',
              description: '覆盖言语与认知训练任务，支持个性化训练方案制定。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '报告中心',
              description: '自动汇总评估与训练数据，生成规范诊疗与康复报告。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            }
          ]
        },
        '言语认知双屏': {
          scope: '适用于同时开展语言障碍与认知障碍诊治的双屏幕台车方案，上下屏协同作业。',
          description: '双屏幕工作站上屏运行语言障碍诊治仪（用户管理、诊断筛查、康复训练、报告中心），下屏运行认知障碍诊治仪（用户管理、报告中心、筛查评估、康复训练），实现言语与认知诊治一体化。',
          features: [
            {
              title: '语言障碍诊治',
              description: '上屏专注语言障碍诊治流程，覆盖诊断筛查、康复训练与报告输出。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '认知障碍诊治',
              description: '下屏专注认知障碍诊治流程，覆盖筛查评估、康复训练与报告管理。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '双屏协同',
              description: '双屏并行展示，提升诊疗效率，适合综合康复科室与多任务场景。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '台车集成',
              description: '集成键盘鼠标、麦克风、打印机与储物柜，移动便捷、部署完整。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            }
          ]
        },
        // 电子肠镜示例数据
        '电子肠镜': {
          scope: '适用于对大肠黏膜进行检查，诊断大肠炎症、溃疡、肿瘤等疾病，并可进行活检和治疗。',
          description: '电子肠镜采用先进的电子成像技术，提供高清的肠道黏膜图像。该设备操作灵活，插入顺畅，患者痛苦小，能够清晰地观察到肠道黏膜的细微变化，为大肠疾病的早期诊断和治疗提供重要依据。',
          features: [
            {
              title: '高清电子成像',
              description: '采用高分辨率电子摄像头，提供清晰、逼真的肠道黏膜图像，细节显示出色。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '超细镜身设计',
              description: '镜身纤细柔软，插入顺畅，有效减少患者不适感。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '广视野角度',
              description: '大角度视野设计，减少检查盲区，提高病变检出率。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '多功能治疗通道',
              description: '配备大直径治疗通道，支持多种内镜下治疗操作。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '智能图像增强',
              description: '内置图像增强功能，能够突出显示微小病变，提高早期病变检出率。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        '电子胃镜': {
          scope: '适用于对食管、胃和十二指肠黏膜进行检查，诊断炎症、溃疡、肿瘤等疾病，并可进行活检和治疗。',
          description: '电子胃镜采用电子成像技术，提供清晰、逼真的上消化道黏膜图像。该设备配备多种治疗附件，可进行息肉切除、止血等内镜下治疗操作，是上消化道疾病诊断和治疗的重要工具。',
          features: [
            {
              title: '高清上消化道成像',
              description: '提供清晰的食管、胃和十二指肠黏膜图像，能够观察到细微的病变。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '舒适插入技术',
              description: '采用先进的插入辅助技术，减少患者在检查过程中的不适感。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '多种治疗附件',
              description: '兼容多种治疗附件，支持息肉切除、止血、黏膜切除等多种内镜下治疗。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '窄带成像功能',
              description: '支持窄带成像技术，能够更清晰地显示黏膜血管结构，提高早期病变检出率。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '快速吸引系统',
              description: '配备高性能吸引泵，能够快速清除视野中的黏液和血液，保持良好视野。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        },
        // 电子阴道镜示例数据
        '4K电子阴道显微镜': {
          scope: '适用于妇科医生对阴道和宫颈进行检查，诊断阴道炎、宫颈炎、宫颈病变等疾病，并可指导活检。',
          description: '4K电子阴道显微镜采用超高清4K成像技术，提供细腻、清晰的阴道和宫颈图像。该设备配备专业的图像分析软件，支持图像的放大、测量和对比，有助于医生发现早期宫颈病变，提高宫颈癌的早期诊断率。',
          features: [
            {
              title: '超高清4K成像',
              description: '采用4K超高清摄像头，提供细腻、清晰的宫颈和阴道黏膜图像，细节展示卓越。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
            },
            {
              title: '智能图像分析',
              description: '内置专业图像分析软件，能够辅助识别异常血管和病变区域。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
            },
            {
              title: '精准测量工具',
              description: '配备精确的图像测量工具，可测量病变大小和位置，辅助活检定位。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
            },
            {
              title: '多光谱成像',
              description: '支持白光、醋酸白试验、碘试验等多种成像模式，全面评估宫颈状况。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
            },
            {
              title: '数字化存储管理',
              description: '支持图像的数字化存储和管理，便于随访对比和病历归档。',
              image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
            }
          ]
        }
      }
    }
  },
  computed: {
    currentImage() {
      return this.productImages[this.currentIndex]
    },
    isCurrentVideo() {
      return this.isVideoUrl(this.currentImage)
    },
    // 仅皮肤镜图像处理工作站（SMS 系列）展示型号
    showProductModel() {
      const title = this.productTitle || ''
      const model = this.productModel || ''
      return title.includes('皮肤镜图像处理工作站')
        || title.includes('SMS-')
        || /^SMS-/.test(model)
    },
    displayProductModel() {
      if (this.productModel && /^SMS-/.test(this.productModel)) {
        return this.productModel
      }
      // 标题里带型号时优先取型号部分
      const match = (this.productTitle || '').match(/SMS-[\wⅠⅡⅢⅣ]+/)
      return match ? match[0] : (this.productModel || '')
    }
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'auto' })
    await this.updateProductInfo()
    this.initScrollReveal()
  },
  watch: {
    '$route.query': {
      async handler() {
        window.scrollTo({ top: 0, behavior: 'auto' })
        await this.updateProductInfo()
      },
      deep: true
    }
  },
  methods: {
    isVideoUrl,

    async updateProductInfo() {
      this.productTitle = this.$route.query.productTitle || ''
      this.productModel = this.$route.query.productModel || ''
      this.currentProductDetails = this.getProductDetails(this.productTitle, this.productModel)

      const fromDb = await this.loadGalleryFromDb()
      if (!fromDb) {
        this.updateProductImagesFallback(this.productTitle)
      }
      this.updateFeatureImages()
    },

    async loadGalleryFromDb() {
      const title = (this.productTitle || '').trim()
      if (!title) return false

      const moduleId = resolveProductModuleId(title)
      if (!moduleId) return false

      try {
        const list = await getProductGalleryByModuleId(PRODUCT_PAGE_NAME, moduleId)
        if (!Array.isArray(list) || list.length === 0) return false

        const urls = list
          .filter((item) => item && item.url)
          .map((item) => {
            const isVideo = item.mediaType === 'video' || isVideoUrl(item.url)
            return isVideo ? item.url : formatImg(item.url)
          })

        if (!urls.length) return false
        this.productImages = urls
        this.currentIndex = 0
        return true
      } catch (e) {
        console.error('加载产品图库失败', e)
        return false
      }
    },
    
    // 根据产品标题和型号获取详细信息
    getProductDetails(title, model) {
      console.log('正在查找产品详情:', { title, model });
      
      // 优先使用完整标题查找
      if (title && this.productDetails[title]) {
        console.log('找到精确匹配:', title);
        return this.productDetails[title]
      }
      
      // 如果没有找到，尝试使用标题中的关键字查找（增强版匹配逻辑）
      if (title) {
        const keys = Object.keys(this.productDetails)
        
        // 1. 精确匹配产品名称中的关键字
        for (const key of keys) {
          if (key.toLowerCase().includes(title.toLowerCase())) {
            console.log('找到关键字匹配:', key);
            return this.productDetails[key]
          }
        }
        
        // 2. 尝试反向匹配（产品标题包含详情键）
        for (const key of keys) {
          if (title.toLowerCase().includes(key.toLowerCase())) {
            console.log('找到反向匹配:', key);
            return this.productDetails[key]
          }
        }
        
        // 3. 智能模糊匹配（根据产品标题中的关键词）
        const keywords = title.split(/[,，\s]+/); // 按逗号、空格等分割关键词
        for (const keyword of keywords) {
          if (keyword.length >= 2) { // 只匹配长度>=2的关键词
            for (const key of keys) {
              if (key.toLowerCase().includes(keyword.toLowerCase())) {
                console.log('找到关键词模糊匹配:', key, '匹配关键词:', keyword);
                return this.productDetails[key]
              }
            }
          }
        }
      }
      
      // 如果型号存在，尝试使用型号查找
      if (model) {
        const keys = Object.keys(this.productDetails)
        for (const key of keys) {
          if (key.includes(model)) {
            console.log('找到型号匹配:', key);
            return this.productDetails[key]
          }
        }
      }
      
      // 如果都没有找到，返回默认的LED光谱治疗仪信息
      console.log('未找到匹配产品，返回默认信息');
      return this.productDetails['双光源LED光谱治疗仪'] || {
        scope: '请联系我们获取产品详细信息',
        description: '请联系我们获取产品详细信息'
      }
    },
    
    updateProductImagesFallback(title) {
      const productImages = {
        'sppfj-001': require('@/assets/uploads/hrys/sppfj-001.png'),
        'handheld': require('@/assets/uploads/hrys/handheld.jpg'),
        'Touchscreen': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731164921814.jpg',
        'portable': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731164822429.png',
        'speechPortable': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731170322849.png',
        'speechSingle': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731170418767.png',
        'speechTablet': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731170425041.jpg',
        'speechDual': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731170432399.png',
        'skinAnalyzer': require('@/assets/uploads/hrys/skinAnalyzer.png'),
        'ElectronicColonoscopy': require('@/assets/uploads/hrys/ElectronicColonoscopy.png'),
        'Colposcope': 'https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731172243424.jpg',
        'default': require('@/assets/images/homepage/spec-pics/spec-pic1.png')
      };
      
      let imageUrl = productImages.default;
      
      if (title.includes('SMS-3000')) {
        this.productImages = [
          require('@/assets/uploads/detailedProduct_imgs/dppfj-001.png'),
          require('@/assets/uploads/detailedProduct_imgs/dppfj-002.png'),
          require('@/assets/uploads/detailedProduct_imgs/dppfj-003.png'),
          require('@/assets/uploads/detailedProduct_imgs/dppfj-004.png'),
          require('@/assets/uploads/detailedProduct_imgs/dppfj-005.png'),
          require('@/assets/uploads/detailedProduct_imgs/dppfj-006.png')
        ];
        this.currentIndex = 0;
        return;
      } else if (title.includes('SMS-6000')) {
        imageUrl = productImages['sppfj-001'];
      } else if (title.includes('言语认知便携式')) {
        imageUrl = productImages.speechPortable;
      } else if (title.includes('言语认知单屏幕')) {
        imageUrl = productImages.speechSingle;
      } else if (title.includes('言语认知平板式')) {
        imageUrl = productImages.speechTablet;
      } else if (title.includes('言语认知双屏')) {
        imageUrl = productImages.speechDual;
      } else if (title.includes('手持式')) {
        imageUrl = productImages['handheld'];
      } else if (title.includes('触屏式')) {
        imageUrl = productImages['Touchscreen'];
      } else if (title.includes('便携式皮肤镜') || (title.includes('便携式') && !title.includes('言语'))) {
        imageUrl = productImages['portable'];
      } else if (title.includes('面部') || title.includes('visia')) {
        imageUrl = productImages['skinAnalyzer'];
      } else if (title.includes('肠镜')) {
        imageUrl = productImages['ElectronicColonoscopy'];
      } else if (title.includes('阴道')) {
        this.productImages = [
          formatImg('https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731172243424.jpg'),
          formatImg('https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731172243426.jpg'),
          formatImg('https://hfqsyl.oss-cn-hangzhou.aliyuncs.com/img/web20260731172243427.jpg')
        ];
        this.currentIndex = 0;
        return;
      }
      
      const url = typeof imageUrl === 'string' && !this.isVideoUrl(imageUrl)
        ? formatImg(imageUrl)
        : imageUrl
      this.productImages = [url, url, url];
      this.currentIndex = 0;
    },
    selectImage(index) {
      // 选择图库中的图片
      this.currentIndex = index
    },
    openImageModal() {
      // 点击主图可以打开大图预览
      // 这里可以实现大图预览功能，暂时保留接口
      console.log('打开图片预览')
    },
    nextImage() {
      // 下一张图片
      this.currentIndex = (this.currentIndex + 1) % this.productImages.length
    },
    prevImage() {
      // 上一张图片
      this.currentIndex = (this.currentIndex - 1 + this.productImages.length) % this.productImages.length
    },
    // 更新产品特性展示内容（「深入了解」固定 5 项，不足则循环复用）
    updateFeatureImages() {
      const defaults = [
        {
          title: '产品特色',
          description: '请联系我们获取该产品的详细特性信息。',
          image: require('@/assets/images/homepage/spec-pics/spec-pic1.png')
        },
        {
          title: '高品质保证',
          description: '我们的产品严格遵循医疗设备标准，确保安全可靠。',
          image: require('@/assets/images/homepage/spec-pics/spec-pic2.png')
        },
        {
          title: '专业技术支持',
          description: '提供全面的技术支持和售后服务，确保设备正常运行。',
          image: require('@/assets/images/homepage/spec-pics/spec-pic3.png')
        },
        {
          title: '持续创新',
          description: '我们不断研发新技术，提升产品性能和用户体验。',
          image: require('@/assets/images/homepage/spec-pics/spec-pic4.jpg')
        },
        {
          title: '完善的培训体系',
          description: '提供专业的设备操作培训，确保医护人员熟练使用。',
          image: require('@/assets/images/homepage/spec-pics/spec-pic5.jpg')
        }
      ]

      const source = (this.currentProductDetails && this.currentProductDetails.features && this.currentProductDetails.features.length)
        ? this.currentProductDetails.features
        : defaults

      this.featureImages = Array.from({ length: 5 }, (_, i) => source[i % source.length])
    },
    
    // 初始化滚动动画
    initScrollReveal() {
      // 为已经存在的元素添加动画类
      this.$nextTick(() => {
        // 获取所有需要动画的元素
        this.animatedElements = document.querySelectorAll('.scroll-animate');
        
        // 初始检查
        this.checkScrollPosition();
        
        // 添加滚动事件监听
        window.addEventListener('scroll', this.handleScroll);
      });
    },
    
    // 处理滚动事件
    handleScroll() {
      this.checkScrollPosition();
    },
    
    // 检查滚动位置并显示元素
    checkScrollPosition() {
      const windowHeight = window.innerHeight;
      const revealPoint = 150; // 元素距离视口底部的阈值
      
      this.animatedElements.forEach((element, index) => {
        if (!element || element.classList.contains('visible')) return;
        
        const elementTop = element.getBoundingClientRect().top;
        
        if (elementTop < windowHeight - revealPoint) {
          // 为每个元素添加延迟，实现一个一个展示的效果
          setTimeout(() => {
            element.classList.add('visible');
          }, index * 300); // 每个元素间隔300ms显示，与ProductApplicationPage保持一致
        }
      });
    },
    
    // 在组件销毁时移除滚动事件监听
    beforeDestroy() {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }
}
</script>

<style scoped>
/* 组件特定样式已移至外部 CSS 文件 */

/* 加载状态样式 */
.features-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.features-loading p {
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

/* 滚动动画相关样式 */
.scroll-animate {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
}

.scroll-animate.visible {
  opacity: 1;
  transform: translateY(0);
}

/* 为子元素添加延迟动画 */
.feature-item {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out;
}

.product-features-section.visible .feature-item {
  opacity: 1;
  transform: translateY(0);
}

/* 为特性项添加交错延迟 */
.feature-item:nth-child(1) { transition-delay: 0.1s; }
.feature-item:nth-child(2) { transition-delay: 0.2s; }
.feature-item:nth-child(3) { transition-delay: 0.3s; }
.feature-item:nth-child(4) { transition-delay: 0.4s; }
.feature-item:nth-child(5) { transition-delay: 0.5s; }
</style>

<style>
/* 引入面包屑导航样式 */
@import '../../style/specificProducts/breadcrumb.css';
/* 引入产品详情样式 */
@import '../../style/specificProducts/product-detail.css';
/* 引入产品特性样式 */
@import '../../style/specificProducts/product-features.css';
</style>